import {
  RichText,
  withDatasourceCheck,
  NextImage,
  ImageField,
  Item,
  Field,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useSitecoreContext } from 'hooks/useSitecoreContext';
import { ComponentProps } from 'lib/component-props';
import { useMemo } from 'react';

type ListItem = Item & {
  fields: {
    image: ImageField;
    title: Field<string>;
    description: Field<string>;
  };
  className?: string;
};

type Fields = {
  image: ImageField;
  icon: ImageField;
  items: ListItem[];
};
type ListProps = ComponentProps & {
  fields: Fields;
};

const List = ({ fields, params, className }: ListProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const isExperienceEditor = sitecoreContext.pageEditing;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isObjectEmpty = (object: any) => {
    return !!Object.keys(object).length;
  };
  const getImage = (listItem: ListItem) => {
    const itemLvlImage = listItem.fields.image;
    return isObjectEmpty(itemLvlImage.value) ? itemLvlImage : fields.image;
  };
  const itemLvlImageNotEmpty = fields.items.some((item) => isObjectEmpty(item.fields.image?.value));
  const imageStyle = useMemo(() => {
    if (itemLvlImageNotEmpty) {
      return true;
    }
    return (
      fields.image?.value?.src &&
      fields.image?.value?.class !== 'scEmptyImage' &&
      params.style === 'unordered' &&
      !fields.icon?.value
    );
  }, [fields, itemLvlImageNotEmpty, params.style]);
  const filteredArray = useMemo(() => {
    return fields.items?.length || isExperienceEditor
      ? fields.items
          .filter((item) => item.fields.title.value || isExperienceEditor)
          .sort((firstItem, secondItem) => {
            if (!itemLvlImageNotEmpty) return 0;
            const firstItemImage = firstItem.fields.image?.value;
            const secondItemImage = secondItem.fields.image?.value;
            if (isObjectEmpty(firstItemImage) && !isObjectEmpty(secondItemImage)) {
              return -1;
            } else if (!isObjectEmpty(firstItemImage) && isObjectEmpty(secondItemImage)) {
              return 1;
            }
            return 0;
          })
      : [];
  }, [fields.items, isExperienceEditor, itemLvlImageNotEmpty]);
  const iconStyle = !!fields.icon?.value && params.style === 'unordered' && !imageStyle;
  const orderedStyle: boolean = params.style === 'ordered';
  const svgExist: boolean = fields.image.value?.['data-type'] === 'image/svg+xml';

  return (
    <div className={`abrdn-list-root ${className}`}>
      <ul
        className={`abrdn-list mb-0 ${[params.style]} ${iconStyle ? 'icon-style' : ''} ${
          imageStyle || svgExist ? 'image-style' : ''
        } ${!iconStyle && !imageStyle && !svgExist ? 'circle-style' : ''} ${
          orderedStyle ? 'ordered-style' : ''
        }`}
      >
        {filteredArray.map((item, index) => (
          <li key={index}>
            <span className="circle background-to-change" />
            {!orderedStyle && (iconStyle || imageStyle || svgExist) && (
              <div className="icon me-2 w-auto">
                {iconStyle && (!imageStyle || !svgExist) && (
                  <em className={`bi bi-${fields.icon.value}`} />
                )}
                {(imageStyle || svgExist) && !iconStyle && (
                  <NextImage
                    field={getImage(item)}
                    className="image background-to-change"
                    width="24"
                    height="24"
                  />
                )}
              </div>
            )}
            <div className="list-item-text">
              {isExperienceEditor ||
                (item.fields && item.fields.title && item.fields.title.value && (
                  <RichText
                    field={item.fields.title}
                    className="body-default background-to-change"
                  />
                ))}
              {isExperienceEditor ||
                (item.fields && item.fields.description && item.fields.description.value && (
                  <RichText field={item.fields.description} className="description body-small" />
                ))}
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default withDatasourceCheck()<ListProps>(List);
